// Shared confirm panel used by every surface that needs an
// in-place delete confirmation — the `DeleteConfirmKebab`
// (notes / follow-ups, CON-253) and the deal more-menu
// (CON-256). Both desktop and mobile render from this one
// component so the copy, layout, testIds, and styles are
// identical everywhere.

export interface DeleteConfirmPanelProps {
  heading: string
  subtitle: string
  isMobile: boolean
  submitting: boolean
  onCancel: () => void
  onDelete: () => void
}

export function DeleteConfirmPanel({
  heading,
  subtitle,
  isMobile,
  submitting,
  onCancel,
  onDelete,
}: DeleteConfirmPanelProps) {
  if (isMobile) {
    return (
      <div className="flex flex-col gap-3" data-testid="delete-confirm">
        <div className="flex flex-col gap-1">
          <span className="text-[15px] font-semibold text-[#1E1B17]">
            {heading}
          </span>
          {subtitle ? (
            <span
              data-testid="delete-confirm-quote"
              className="text-[#7A746B] text-[13px] leading-5"
            >
              {subtitle}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="delete-confirm-cancel"
            onClick={onCancel}
            disabled={submitting}
            className="w-full rounded-full py-2.5 px-4 bg-[#F2EDE3] text-[#1E1B17] text-[15px] font-semibold cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="delete-confirm-delete"
            onClick={onDelete}
            disabled={submitting}
            className="w-full rounded-full py-2.5 px-4 bg-[#C04F3D] text-white text-[15px] font-semibold cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2.5 p-1" data-testid="delete-confirm">
      <div className="flex flex-col gap-1">
        <span className="text-[13px] font-medium text-[#1E1B17]">
          {heading}
        </span>
        {subtitle ? (
          <span
            data-testid="delete-confirm-quote"
            className="text-[#7A746B] text-[12px] leading-4 truncate"
            title={subtitle}
          >
            {subtitle}
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="delete-confirm-cancel"
          onClick={onCancel}
          disabled={submitting}
          className="text-[12px] font-medium text-[#9A8F7E] cursor-pointer hover:underline disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="delete-confirm-delete"
          onClick={onDelete}
          disabled={submitting}
          className="ml-auto rounded-lg py-1.5 px-3 bg-[#C53030] text-white text-[12px] font-semibold cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:opacity-50"
        >
          Delete
        </button>
      </div>
    </div>
  )
}
